import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Icon from './Icon';
import Avatar from './Avatar';
import CustomSelect from './CustomSelect';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import { useSortableTable } from '../hooks/useSortableTable';
import { renderFormattedText } from '../utils/simpleRichText';
import '../styles/LiveKitMeetings.css';

// Same camera glyph used in the Velvet Meeting Lobby / Meeting Room controls
// (see MeetingLobby.js's CameraIcon) - the font icon set doesn't have a match.
const CameraIcon = ({ className }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 26 26" fill="none">
    <path d="M5.41667 6.5H15.1667C16.3633 6.5 17.3333 7.47005 17.3333 8.66667V17.3333C17.3333 18.53 16.3633 19.5 15.1667 19.5H5.41667C4.22005 19.5 3.25 18.53 3.25 17.3333V8.66667C3.25 7.47005 4.22005 6.5 5.41667 6.5Z" strokeWidth="1.95" stroke="currentColor" />
    <path d="M17.332 10.834L22.7487 7.58398V18.4173L17.332 15.1673" strokeWidth="1.95" strokeLinejoin="round" stroke="currentColor" />
  </svg>
);

// dl.svg - shared download glyph for Download buttons/links
const DownloadIcon = ({ className = '' }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ display: 'block', opacity: 1, transform: 'rotate(0deg)', flexShrink: 0 }}
  >
    <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" />
    <path d="M11.3346 6.66667L8.0013 10L4.66797 6.66667M8.0013 10V2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor" />
  </svg>
);

const MEETING_TYPE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'content', label: 'Content' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'checkin', label: 'Check-in & Review' },
];

const DATE_RANGE_OPTIONS = [
  { value: 'last7', label: 'Last 7 Days', days: 7 },
  { value: 'last30', label: 'Last 30 Days', days: 30 },
  { value: 'last90', label: 'Last 90 Days', days: 90 },
  { value: 'all', label: 'All Time', days: null },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)', short: 'Eastern' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)', short: 'Central' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)', short: 'Mountain' },
  { value: 'America/Phoenix', label: 'Arizona', short: 'Arizona' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', short: 'Pacific' },
  { value: 'America/Anchorage', label: 'Alaska', short: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii', short: 'Hawaii' },
  { value: 'UTC', label: 'UTC', short: 'UTC' },
  { value: 'Europe/London', label: 'London', short: 'London' },
  { value: 'Europe/Paris', label: 'Paris, Berlin', short: 'Paris' },
  { value: 'Asia/Dubai', label: 'Dubai', short: 'Dubai' },
  { value: 'Asia/Kolkata', label: 'Mumbai, New Delhi', short: 'India' },
  { value: 'Asia/Shanghai', label: 'Beijing, Shanghai', short: 'China' },
  { value: 'Asia/Tokyo', label: 'Tokyo', short: 'Tokyo' },
  { value: 'Australia/Sydney', label: 'Sydney', short: 'Sydney' },
];

const RECORDINGS_TIMEZONE_STORAGE_KEY = 'velvet_recordings_timezone';

const getDetectedTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

const getTimezoneShortLabel = (tz) => {
  const known = TIMEZONE_OPTIONS.find((t) => t.value === tz);
  if (known) return known.short;
  const city = tz?.split('/').pop()?.replace(/_/g, ' ');
  return city || tz;
};

const LiveKitRecordings = ({ user, activeAgency }) => {
  const modal = useModal();
  const { selectedOrganization, selectedBrand, loading: orgBrandLoading } = useOrgBrand();

  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTypeTab, setActiveTypeTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('last30');
  const [transcribingMeetings, setTranscribingMeetings] = useState({});
  const [generatingIdeas, setGeneratingIdeas] = useState({});
  const [userTimezone, setUserTimezone] = useState(() => {
    try {
      return localStorage.getItem(RECORDINGS_TIMEZONE_STORAGE_KEY) || getDetectedTimezone();
    } catch {
      return getDetectedTimezone();
    }
  });
  const [timezoneMenuOpen, setTimezoneMenuOpen] = useState(false);

  // Detail panel (slides out from the right when a row's "..." is clicked)
  const [detailMeetingId, setDetailMeetingId] = useState(null);
  const [detailTab, setDetailTab] = useState('recordings');
  const [detailMenuOpen, setDetailMenuOpen] = useState(false);
  const [detailNotes, setDetailNotes] = useState({ loading: false, items: [], loadedFor: null });
  const [detailChat, setDetailChat] = useState({ loading: false, items: [], loadedFor: null });

  const isSuperAdmin = user?.is_super_admin;

  const orgId = selectedOrganization?.id;
  const brandId = selectedBrand?.id;

  // Fetch meetings
  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        organization_id: orgId,
        brand_id: brandId
      };

      const response = await axios.get('/api/livekit/meetings', {
        params,
        withCredentials: true
      });

      setMeetings(response.data.meetings || []);
    } catch (err) {
      console.error('Error fetching LiveKit meetings:', err);
      if (err.response?.status === 403) {
        modal.error('Access denied. You do not have permission to view meetings.');
      } else {
        modal.error('Failed to load meetings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filterStatus, orgId, brandId]);

  useEffect(() => {
    // Wait for the org/brand context to finish restoring the saved selection
    // before fetching - otherwise this fires with no org/brand filter and
    // briefly (or permanently, if the orgs/brands never settle) shows every
    // meeting across every org/brand instead of just the selected one.
    if (orgBrandLoading) {
      return;
    }
    if (isSuperAdmin && !activeAgency) {
      setLoading(false);
      return;
    }
    fetchMeetings();
  }, [filterStatus, activeAgency, isSuperAdmin, orgBrandLoading, fetchMeetings]);

  const handleRefresh = () => {
    fetchMeetings();
  };

  useEffect(() => {
    if (!detailMenuOpen) return;
    const closeMenu = () => setDetailMenuOpen(false);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [detailMenuOpen]);

  useEffect(() => {
    if (!timezoneMenuOpen) return;
    const closeMenu = () => setTimezoneMenuOpen(false);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [timezoneMenuOpen]);

  // Lazily load Notes/Chat the first time their tab is opened for a given meeting.
  useEffect(() => {
    if (!detailMeetingId) return;

    if (detailTab === 'notes' && detailNotes.loadedFor !== detailMeetingId) {
      setDetailNotes({ loading: true, items: [], loadedFor: detailMeetingId });
      axios.get(`/api/livekit/meetings/${detailMeetingId}/notes`, { withCredentials: true })
        .then((res) => setDetailNotes({ loading: false, items: res.data.notes || [], loadedFor: detailMeetingId }))
        .catch((err) => {
          console.error('Error loading meeting notes:', err);
          setDetailNotes({ loading: false, items: [], loadedFor: detailMeetingId });
          modal.error(err.response?.data?.error || 'Failed to load meeting notes');
        });
    }

    if (detailTab === 'chat' && detailChat.loadedFor !== detailMeetingId) {
      setDetailChat({ loading: true, items: [], loadedFor: detailMeetingId });
      axios.get(`/api/livekit/meetings/${detailMeetingId}/chat`, { withCredentials: true })
        .then((res) => setDetailChat({ loading: false, items: res.data.messages || [], loadedFor: detailMeetingId }))
        .catch((err) => {
          console.error('Error loading meeting chat:', err);
          setDetailChat({ loading: false, items: [], loadedFor: detailMeetingId });
          modal.error(err.response?.data?.error || 'Failed to load meeting chat');
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailMeetingId, detailTab]);

  const openDetailPanel = (meetingId) => {
    setDetailMeetingId(meetingId);
    setDetailTab('recordings');
    setDetailMenuOpen(false);
  };

  const closeDetailPanel = () => {
    setDetailMeetingId(null);
    setDetailMenuOpen(false);
  };

  const handleTimezoneChange = (tz) => {
    setUserTimezone(tz);
    setTimezoneMenuOpen(false);
    try {
      localStorage.setItem(RECORDINGS_TIMEZONE_STORAGE_KEY, tz);
    } catch {
      // ignore storage failures (e.g. private browsing)
    }
  };

  const handleDelete = async (meetingId) => {
    const result = await modal.confirm('Are you sure you want to delete this meeting? This action cannot be undone.', 'Delete Meeting');
    if (!result) {
      return;
    }

    // Remove it from the list immediately - the backend walks every S3
    // recording file for this meeting before it responds, which can take
    // a while, and there's no reason the UI should sit frozen waiting on
    // that (or pay for a full fetchMeetings() refetch) when we already
    // know the outcome we want to show. Roll the row back only if the
    // delete actually fails.
    const previousMeetings = meetings;
    setMeetings((prev) => prev.filter((m) => m.meeting_id !== meetingId));

    try {
      await axios.delete(`/api/livekit/meetings/${meetingId}`, {
        withCredentials: true
      });
      modal.success('Meeting deleted successfully!');
    } catch (err) {
      console.error('Error deleting meeting:', err);
      modal.error(err.response?.data?.error || 'Failed to delete meeting');
      setMeetings(previousMeetings);
    }
  };

  const handleTranscribe = async (meetingId) => {
    const meeting = meetings.find(m => m.meeting_id === meetingId);
    if (isSuperAdmin && meeting?.transcript_status === 'completed') {
      const result = await modal.confirm('This meeting already has a transcript.\nAre you sure you want to regenerate it? This will overwrite the existing transcript.', 'Regenerate Transcript');
      if (!result) {
        return;
      }
    }

    setTranscribingMeetings(prev => ({ ...prev, [meetingId]: true }));

    try {
      const response = await axios.post(
        `/api/livekit/meetings/${meetingId}/transcribe`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        modal.success('Transcription completed successfully!');
        fetchMeetings();
      } else {
        modal.error(response.data.error || 'Failed to transcribe');
      }
    } catch (err) {
      console.error('Error transcribing:', err);
      modal.error(err.response?.data?.error || 'Failed to transcribe meeting');
    } finally {
      setTranscribingMeetings(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const canTranscribe = (meeting) => {
    const hasRecordings = meeting.recording_count > 0;

    if (isSuperAdmin) {
      return hasRecordings;
    }

    const transcriptNotDone = !meeting.transcript_status ||
                              meeting.transcript_status === 'pending' ||
                              meeting.transcript_status === 'failed';
    return hasRecordings && transcriptNotDone;
  };

  const handleGenerateIdeas = async (meetingId) => {
    const meeting = meetings.find(m => m.meeting_id === meetingId);
    if (isSuperAdmin && meeting?.has_ideas_generated) {
      const result = await modal.confirm('This meeting already has ideas generated.\nGenerate another batch? This adds a new set of ideas alongside the existing ones - it does not overwrite them.', 'Regenerate Ideas');
      if (!result) {
        return;
      }
    } else {
      const result = await modal.confirm('Generate AI-powered content ideas from this meeting transcript?', 'Generate Ideas');
      if (!result) {
        return;
      }
    }

    setGeneratingIdeas(prev => ({ ...prev, [meetingId]: true }));

    try {
      const response = await axios.post('/api/ideas/generate', {
        meeting_id: meetingId,
        source_type: 'livekit'
      }, { withCredentials: true });

      if (response.data.success) {
        if (response.data.status === 'partial_success') {
          modal.success(
            `${response.data.reason || `Generated ${response.data.ideas_count} quality ideas from the transcript.`} Go to Ideas > Review Ideas to review them.`
          );
        } else {
          modal.success(`Successfully generated ${response.data.ideas_count} ideas! Go to Ideas > Review Ideas to review them.`);
        }
        fetchMeetings();
      } else {
        modal.error(response.data.error || 'Failed to generate ideas');
      }
    } catch (err) {
      console.error('Error generating ideas:', err);
      const errorData = err.response?.data;

      if (errorData?.missing_assignment) {
        modal.error('Cannot generate ideas: This meeting needs to be assigned to an organization and brand first.');
      } else if (errorData?.empty_brand_document) {
        modal.error(`Cannot generate ideas: The brand profile for "${errorData.brand_name}" is empty.\n\nPlease go to Brand Identity and fill in the brand profile with information about the brand's audience, messaging, and content pillars.`);
      } else {
        modal.error(errorData?.error || 'Failed to generate ideas.\nMake sure the meeting has a transcript.');
      }
    } finally {
      setGeneratingIdeas(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const canGenerateIdeas = (meeting) => {
    // Super admins can always regenerate, e.g. to test prompt/logic changes
    // without re-recording a meeting
    const hasTranscript = meeting.transcript_status === 'completed' && meeting.transcript;
    const hasOrg = meeting.organization?.name || meeting.organization_id;
    const hasBrand = meeting.brand?.name || meeting.brand_id;

    if (isSuperAdmin) {
      return hasTranscript && hasOrg && hasBrand;
    }

    const ideasNotGenerated = !meeting.has_ideas_generated;
    return hasTranscript && hasOrg && hasBrand && ideasNotGenerated;
  };

  const getGenerateIdeasTooltip = (meeting) => {
    const hasTranscript = meeting.transcript_status === 'completed' && meeting.transcript;
    const hasOrg = meeting.organization?.name || meeting.organization_id;
    const hasBrand = meeting.brand?.name || meeting.brand_id;

    if (meeting.has_ideas_generated) {
      return isSuperAdmin
        ? "Ideas already generated - click to generate another batch (Super Admin)"
        : "Ideas already generated from this meeting";
    }
    if (!hasTranscript) {
      return "Transcript required to generate ideas";
    }
    if (!hasOrg || !hasBrand) {
      const missing = [];
      if (!hasOrg) missing.push("organization");
      if (!hasBrand) missing.push("brand");
      return `Assign ${missing.join(" and ")} first`;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      timeZone: userTimezone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  // "Oct 28, 2026 · 5:36 PM" - used for the meeting row's "When" column.
  const formatMeetingWhen = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    const datePart = date.toLocaleDateString('en-US', { timeZone: userTimezone, month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { timeZone: userTimezone, hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} · ${timePart}`;
  };

  const getMeetingTypePillLabel = (type) =>
    MEETING_TYPE_TABS.find((t) => t.value === (type || 'content'))?.label || 'Content';

  const renderRecordingsCell = (meeting) => {
    if (meeting.room_status === 'scheduled') {
      return <span className="vr-recording-not-started">Not Started Yet</span>;
    }

    if (meeting.room_status === 'active') {
      return (
        <span className="vr-recording-live">
          <span className="vr-recording-live-dot"></span>
          Recording Still Open
        </span>
      );
    }

    const audioTotal = meeting.audio_files?.length || 0;
    // Audio recordings finalize all at once (no per-participant progress from
    // the server), so the expected count is the meeting's participant count.
    const audioExpectedTotal = meeting.participants?.length || audioTotal || 1;
    // merge_status/recording_status can get stuck on 'processing' (a backend
    // race between ending the meeting and the egress-finished webhook) even
    // though every expected file already landed - once the count matches,
    // trust the files over the stale status flag.
    const audioStillExpected = audioTotal < audioExpectedTotal;
    const audioUploading = audioStillExpected && (meeting.merge_status === 'processing' || meeting.recording_status === 'processing');
    const audioFailed = audioStillExpected && (meeting.merge_status === 'failed' || meeting.recording_status === 'failed');

    const localParticipants = Object.values(meeting.local_recording_participants || {});
    const localTotal = localParticipants.length;
    // Unlike the server line above, this denominator used to be the number of
    // participants who ever reported in (localTotal) - so a participant whose
    // local recorder never started at all (never even reporting a status)
    // silently shrank the denominator instead of showing up as missing.
    const localExpectedTotal = meeting.participants?.length || localTotal || 1;
    const localCompleted = localParticipants.filter((p) => p.status === 'completed').length;
    const localFailed = localParticipants.filter((p) => p.status === 'failed').length;
    const localUploading = localParticipants.some((p) => p.status === 'uploading' || p.status === 'processing');

    if (audioTotal === 0 && !audioUploading && !audioFailed && localTotal === 0) {
      return <span className="no-recording">-</span>;
    }

    return (
      <div className="vr-recording-lines">
        {(audioTotal > 0 || audioUploading || audioFailed) && (
          <div className="vr-recording-line">
            <Icon
              name="mic"
              size="0.9rem"
              className={`vr-recording-icon${audioUploading ? ' vr-recording-icon-uploading' : audioFailed ? ' vr-recording-icon-failed' : (audioTotal >= audioExpectedTotal ? ' vr-recording-icon-success' : '')}`}
            />
            <span>Server {audioTotal} of {audioExpectedTotal}</span>
            {audioUploading ? (
              <span className="vr-recording-uploading-text">&middot; uploading</span>
            ) : audioFailed ? (
              <span className="vr-recording-failed-text">&middot; failed</span>
            ) : null}
          </div>
        )}
        {localTotal > 0 && (
          <div className="vr-recording-line">
            <CameraIcon
              className={`vr-recording-icon${localUploading ? ' vr-recording-icon-uploading' : localFailed > 0 ? ' vr-recording-icon-failed' : (localCompleted === localExpectedTotal ? ' vr-recording-icon-success' : '')}`}
            />
            <span>Local {localCompleted} of {localExpectedTotal}</span>
            {localUploading ? (
              <span className="vr-recording-uploading-text">&middot; uploading</span>
            ) : localFailed > 0 ? (
              <span className="vr-recording-failed-text">&middot; failed</span>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'scheduled':
        return 'status-badge-secondary';
      case 'active':
        return 'status-badge-warning';
      default:
        return 'status-badge-success';
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status) {
      case 'scheduled':
        return 'Scheduled';
      case 'active':
        return 'In Progress';
      default:
        return 'Ready for Ideas';
    }
  };

  const getTranscriptStatusText = (status) => {
    switch(status) {
      case 'completed':
        return 'Ready';
      case 'processing':
        return 'Processing';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      default:
        return '-';
    }
  };

  const getTranscriptStatusBadgeClass = (status) => {
    switch(status) {
      case 'completed':
        return 'status-badge-success';
      case 'processing':
        return 'status-badge-warning';
      case 'failed':
        return 'status-badge-danger';
      default:
        return 'status-badge-secondary';
    }
  };

  const copyTranscript = async (transcript) => {
    if (!transcript) return;
    try {
      await navigator.clipboard.writeText(transcript);
      modal.toast('Transcript copied to clipboard', 'success');
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  };

  const formatElapsed = (totalSeconds) => {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
    const m = Math.floor(safeSeconds / 60);
    const s = safeSeconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Meeting duration, preferring actual join/leave times over the schedule.
  const formatMeetingDuration = (meeting) => {
    const start = meeting.actual_start_time || meeting.scheduled_start;
    const end = meeting.actual_end_time || meeting.scheduled_end;
    if (!start || !end) return null;
    const ms = new Date(end) - new Date(start);
    if (!(ms > 0)) return null;
    const totalMinutes = Math.round(ms / 60000);
    if (totalMinutes < 60) return `${totalMinutes} min`;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h} hr${h > 1 ? 's' : ''}${m ? ` ${m} min` : ''}`;
  };

  // "41:12" - per-file duration (seconds) shown next to a recording.
  const formatFileDuration = (seconds) => {
    if (seconds == null) return null;
    const total = Math.round(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Best-effort file type label (e.g. "MP3") sniffed from the download URL,
  // since the API doesn't return an explicit format name.
  const getFileExtLabel = (file) => {
    const url = file?.formats?.mp3 || file?.url || file?.s3_key;
    if (!url) return null;
    const match = String(url).split('?')[0].match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toUpperCase() : null;
  };

  // Total time cut from the transcript by pauses - only counts pauses that
  // have actually been resumed (an in-progress pause has no end yet).
  const getPausedSummary = (meeting) => {
    const history = (meeting.pause_history || []).filter((p) => p.paused_at && p.resumed_at);
    if (history.length === 0) return null;
    const totalMs = history.reduce((sum, p) => sum + (new Date(p.resumed_at) - new Date(p.paused_at)), 0);
    return { count: history.length, minutes: Math.round(totalMs / 60000) };
  };

  const getParticipantRoleLabel = (meeting, participant) => {
    if (participant.is_guest) return 'Guest';
    if (meeting.created_by && participant.user_id && String(participant.user_id) === String(meeting.created_by)) {
      return 'Host';
    }
    return 'Participant';
  };

  const handleDownloadAll = (meeting) => {
    const urls = [];
    (meeting.audio_files || []).forEach((f) => {
      const url = f.formats?.mp3 || f.url;
      if (url) urls.push(url);
    });
    Object.values(meeting.local_recording_participants || {}).forEach((p) => {
      if (p.status === 'completed' && p.url) urls.push(p.url);
    });
    urls.forEach((url) => window.open(url, '_blank', 'noopener'));
  };

  // Opens the real /join-meeting URL in a new tab instead of swapping in
  // <LiveKitMeetingRoom> via local state - that state-only approach meant a
  // mid-meeting page refresh landed back on this plain dashboard with no
  // trace you'd been in a meeting (the meeting itself was never affected -
  // only this tab's memory of being in it). /join-meeting/:meetingId is a
  // real route (see App.js), so a refresh there reconnects straight back in.
  // Opening in a new tab keeps the dashboard around in the original tab.
  const handleJoinMeeting = (meetingId) => {
    window.open(`/join-meeting/${meetingId}`, '_blank');
  };

  // Search/date apply before the type tab so each tab's count reflects
  // what that tab would show given the other active filters.
  const matchesSearch = (meeting) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    if ((meeting.title || '').toLowerCase().includes(query)) return true;
    const participants = Object.values(meeting.local_recording_participants || {});
    return participants.some((p) => (p.participant_name || '').toLowerCase().includes(query));
  };

  const matchesDateRange = (meeting) => {
    const option = DATE_RANGE_OPTIONS.find((o) => o.value === dateRange);
    if (!option || !option.days) return true;
    if (!meeting.scheduled_start) return true;
    const cutoff = Date.now() - option.days * 24 * 60 * 60 * 1000;
    return new Date(meeting.scheduled_start).getTime() >= cutoff;
  };

  const searchAndDateFilteredMeetings = meetings.filter((m) => matchesSearch(m) && matchesDateRange(m));

  const typeCounts = searchAndDateFilteredMeetings.reduce((acc, m) => {
    const type = m.meeting_type || 'content';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const visibleMeetings = activeTypeTab === 'all'
    ? searchAndDateFilteredMeetings
    : searchAndDateFilteredMeetings.filter((m) => (m.meeting_type || 'content') === activeTypeTab);

  const { sortedData: sortedMeetings } = useSortableTable(visibleMeetings);

  const meetingSections = [
    { key: 'in-progress', label: 'In Progress', dot: 'yellow', items: sortedMeetings.filter((m) => m.room_status === 'active') },
    { key: 'upcoming', label: 'Upcoming', dot: 'grey', items: sortedMeetings.filter((m) => m.room_status === 'scheduled') },
    { key: 'previous', label: 'Previous', dot: 'blue', items: sortedMeetings.filter((m) => m.room_status !== 'active' && m.room_status !== 'scheduled') },
  ];

  const detailMeeting = meetings.find((m) => m.meeting_id === detailMeetingId) || null;

  const renderMeetingRow = (meeting) => {
    const isJoinable = meeting.room_status === 'scheduled' || meeting.room_status === 'active';
    const visibleParticipants = (meeting.participants || []).slice(0, 4);
    const extraParticipants = (meeting.participants?.length || 0) - visibleParticipants.length;

    return (
      <tr key={meeting.meeting_id} className="vr-meeting-row">
        <td className="title-cell">
          <div className="vr-meeting-title-wrap">
            <strong>{meeting.title || 'Untitled Meeting'}</strong>
            <span className={`vr-type-pill vr-type-pill-${meeting.meeting_type || 'content'}`}>
              <span className="vr-type-pill-dot"></span>
              {getMeetingTypePillLabel(meeting.meeting_type)}
            </span>
          </div>
        </td>
        <td className="date-cell">
          {formatMeetingWhen(meeting.scheduled_start)}
        </td>
        <td className="participants-cell">
          {visibleParticipants.length > 0 ? (
            <div className="vr-avatar-stack">
              {visibleParticipants.map((p, index) => (
                <div className="vr-avatar-stack-item" key={p.participant_identity || index}>
                  <Avatar user={{ _id: p.user_id, agency_id: user?.agency_id }} size="xs" />
                </div>
              ))}
              {extraParticipants > 0 && (
                <div className="vr-avatar-stack-item vr-avatar-overflow">+{extraParticipants}</div>
              )}
            </div>
          ) : (
            <span className="no-recording">-</span>
          )}
        </td>
        <td className="recording-cell">
          {renderRecordingsCell(meeting)}
        </td>
        <td className="status-cell">
          <div className={`status-badge ${getStatusBadgeClass(meeting.room_status)}`}>
            {getStatusDisplayText(meeting.room_status)}
          </div>
        </td>
        <td className="vr-actions-cell">
          <div className="vr-actions-row">
            {isJoinable ? (
              <button
                className="vr-primary-btn vr-open-lobby-btn"
                onClick={() => handleJoinMeeting(meeting.meeting_id)}
              >
                Open Lobby
              </button>
            ) : (
              <button
                className="vr-primary-btn vr-generate-ideas-btn"
                onClick={() => handleGenerateIdeas(meeting.meeting_id)}
                title={getGenerateIdeasTooltip(meeting)}
                disabled={generatingIdeas[meeting.meeting_id] || !canGenerateIdeas(meeting)}
              >
                {generatingIdeas[meeting.meeting_id] ? (
                  <span className="spinner-small"></span>
                ) : (
                  <>
                    <Icon name="lightbulb" size="0.9rem" />
                    Generate Ideas
                  </>
                )}
              </button>
            )}

            <button
              type="button"
              className="vr-overflow-btn"
              title="View details"
              onClick={(e) => {
                e.stopPropagation();
                openDetailPanel(meeting.meeting_id);
              }}
            >
              <span className="vr-overflow-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const topBar = (
    <div className="vr-recordings-bar">
      <div className="vr-type-tabs">
        {MEETING_TYPE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={`vr-type-tab${activeTypeTab === tab.value ? ' active' : ''}`}
            onClick={() => setActiveTypeTab(tab.value)}
          >
            {tab.label} {tab.value === 'all' ? searchAndDateFilteredMeetings.length : (typeCounts[tab.value] || 0)}
          </button>
        ))}
      </div>
      <div className="vr-top-bar-right">
        <div className="vr-search">
          <Icon name="search" size="0.9rem" />
          <input
            type="text"
            className="vr-search-input"
            placeholder="Search meetings or people"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <CustomSelect
          className="vr-status-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">Status: Any</option>
          <option value="scheduled">Scheduled</option>
          <option value="active">In Progress</option>
          <option value="ended">Completed</option>
        </CustomSelect>
        <CustomSelect
          className="vr-date-select"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        >
          {DATE_RANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              <Icon name="calendar" size="0.85rem" /> {opt.label}
            </option>
          ))}
        </CustomSelect>
      </div>
    </div>
  );

  const bottomBar = (
    <div className="vr-bottom-bar">
      <div className="vr-bottom-bar-count">
        {visibleMeetings.length} {visibleMeetings.length === 1 ? 'meeting' : 'meetings'}
      </div>
      <div className="vr-bottom-bar-timezone">
        <span>Times shown in your time zone ({getTimezoneShortLabel(userTimezone)})</span>
        <span>&middot;</span>
        <div className="vr-timezone-picker">
          <button
            type="button"
            className="vr-timezone-change-btn"
            onClick={(e) => {
              e.stopPropagation();
              setTimezoneMenuOpen((prev) => !prev);
            }}
          >
            change
          </button>
          {timezoneMenuOpen && (
            <div className="vr-timezone-menu" onClick={(e) => e.stopPropagation()}>
              {TIMEZONE_OPTIONS.map((tz) => (
                <button
                  key={tz.value}
                  type="button"
                  className={`vr-timezone-menu-item${tz.value === userTimezone ? ' active' : ''}`}
                  onClick={() => handleTimezoneChange(tz.value)}
                >
                  {tz.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="super-admin-internal-layout vr-recordings-panel">
        {topBar}
        <div className="internal-content">
          <div className="livekit-meetings-container">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading meetings...</p>
            </div>
          </div>
        </div>
        {bottomBar}
      </div>
    );
  }

  return (
    <div className="super-admin-internal-layout vr-recordings-panel">
      {topBar}
      <div className="internal-content">
      <div className="livekit-meetings-container">
      <div className="meetings-content-wrapper">

        {meetings.length === 0 ? (
          <div className="empty-state-meetings">
            <div className="empty-icon"><Icon name="velvet" size="3rem" /></div>
            <h3>No Meetings Found</h3>
            <p>There are no Velvet meetings yet.</p>
            <p className="hint-text">
              Use the "Schedule Meeting" tab to create your first meeting.
            </p>
          </div>
        ) : visibleMeetings.length === 0 ? (
          <div className="empty-state-meetings">
            <div className="empty-icon"><Icon name="velvet" size="3rem" /></div>
            <h3>No Meetings Found</h3>
            <p>No meetings match your current filters.</p>
          </div>
        ) : (
          <>
            {/* Filter/Refresh controls hidden for now
            <div className="recordings-header-controls">
              <div className="header-actions">
                <CustomSelect
                  className="filter-status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="active">In Progress</option>
                  <option value="ended">Completed</option>
                </CustomSelect>
                <button className="btn-refresh" onClick={handleRefresh}>
                  Refresh
                </button>
              </div>
            </div>
            */}

            <div className="meetings-table-card">
              <table className="meetings-table">
                <thead>
                  <tr>
                    <th>Meeting</th>
                    <th>When</th>
                    <th>Participants</th>
                    <th>Recordings</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {meetingSections.map((section) => (
                    <React.Fragment key={section.key}>
                      <tr className="vr-section-divider-row">
                        <td className="vr-section-divider" colSpan={6}>
                          <span className={`vr-section-dot vr-section-dot-${section.dot}`}></span>
                          {section.label} &middot; {section.items.length}
                        </td>
                      </tr>
                      {section.items.map(renderMeetingRow)}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      </div>
      </div>
      {bottomBar}
      {detailMeeting && (
        <div className="vr-detail-overlay" onClick={closeDetailPanel}>
          <div className="vr-detail-panel" onClick={(e) => e.stopPropagation()}>
            <div className="vr-detail-header">
              <span className={`vr-type-pill vr-detail-pill vr-type-pill-${detailMeeting.meeting_type || 'content'}`}>
                <span className="vr-type-pill-dot"></span>
                {getMeetingTypePillLabel(detailMeeting.meeting_type)}
              </span>
              <div className="vr-detail-header-actions">
                <div className="vr-overflow-wrapper">
                  <button
                    type="button"
                    className="vr-detail-icon-btn"
                    title="More actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailMenuOpen((prev) => !prev);
                    }}
                  >
                    <span className="vr-overflow-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </span>
                  </button>
                  {detailMenuOpen && (
                    <div className="vr-overflow-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="vr-overflow-menu-item"
                        disabled={transcribingMeetings[detailMeeting.meeting_id] || !canTranscribe(detailMeeting)}
                        title={
                          !canTranscribe(detailMeeting)
                            ? "No recordings to transcribe"
                            : (isSuperAdmin && detailMeeting.transcript_status === 'completed')
                              ? "Regenerate transcript (Super Admin)"
                              : "Transcribe recording"
                        }
                        onClick={() => {
                          handleTranscribe(detailMeeting.meeting_id);
                          setDetailMenuOpen(false);
                        }}
                      >
                        <Icon name="doc" size="0.9rem" />
                        {transcribingMeetings[detailMeeting.meeting_id]
                          ? 'Transcribing…'
                          : (isSuperAdmin && detailMeeting.transcript_status === 'completed')
                            ? 'Re-Transcribe'
                            : 'Transcribe'}
                      </button>
                    </div>
                  )}
                </div>
                <button type="button" className="vr-detail-icon-btn" title="Close" onClick={closeDetailPanel}>
                  <Icon name="close" size="1rem" />
                </button>
              </div>
            </div>

            <div className="vr-detail-body">
              <div className="vr-detail-top-section">
              <div className="vr-detail-heading">
                <h2 className="vr-detail-title">{detailMeeting.title || 'Untitled Meeting'}</h2>
                <div className="vr-detail-meta">
                  {formatMeetingWhen(detailMeeting.scheduled_start)}
                  {formatMeetingDuration(detailMeeting) ? ` · ${formatMeetingDuration(detailMeeting)}` : ''}
                  {` · ${detailMeeting.participants?.length || 0} participant${(detailMeeting.participants?.length || 0) === 1 ? '' : 's'}`}
                </div>
              </div>

              <div className="vr-detail-stepper">
                {(() => {
                  const recordedDone = (detailMeeting.audio_files?.length > 0) ||
                    Object.keys(detailMeeting.local_recording_participants || {}).length > 0;
                  const transcribedDone = detailMeeting.transcript_status === 'completed';
                  const transcribedActive = detailMeeting.transcript_status === 'processing';
                  const ideasDone = !!detailMeeting.has_ideas_generated;
                  const ideasActive = transcribedDone && !ideasDone;
                  const steps = [
                    { label: 'Recorded', icon: 'ok', done: recordedDone },
                    { label: 'Transcribed', icon: 'ok', done: transcribedDone, active: transcribedActive },
                    { label: 'Ideas', icon: 'lightbulb', done: ideasDone, active: ideasActive },
                    { label: 'Content', icon: 'content', done: false },
                  ];
                  return steps.map((step, i) => (
                    <React.Fragment key={step.label}>
                      {i > 0 && <span className={`vr-step-line${steps[i - 1].done ? ' done' : ''}`} />}
                      <span className={`vr-step${step.done ? ' done' : step.active ? ' active' : ''}`}>
                        <span className="vr-step-icon">
                          <Icon name={step.icon} size="0.6rem" />
                        </span>
                        <span className="vr-step-label">{step.label}</span>
                      </span>
                    </React.Fragment>
                  ));
                })()}
              </div>

              {detailMeeting.transcript_status === 'completed' && canGenerateIdeas(detailMeeting) && (
                <div className="vr-detail-banner">
                  <div className="vr-detail-banner-text">
                    <strong>Transcript is ready</strong>
                    <span>
                      {getMeetingTypePillLabel(detailMeeting.meeting_type)} meeting — ideas are generated from the transcript, not the local files.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="vr-primary-btn vr-generate-ideas-btn"
                    onClick={() => handleGenerateIdeas(detailMeeting.meeting_id)}
                    disabled={generatingIdeas[detailMeeting.meeting_id]}
                  >
                    {generatingIdeas[detailMeeting.meeting_id] ? (
                      <span className="spinner-small"></span>
                    ) : (
                      <>
                        <Icon name="lightbulb" size="0.9rem" />
                        Generate ideas
                      </>
                    )}
                  </button>
                </div>
              )}
              </div>

              <div className="vr-detail-tabs">
                {[
                  { key: 'recordings', label: 'Recordings', icon: 'mic' },
                  { key: 'transcript', label: 'Transcript', icon: 'doc' },
                  { key: 'notes', label: 'Notes', icon: 'pencil' },
                  { key: 'chat', label: 'Chat', icon: 'comment-empty', badge: detailMeeting.chat_messages?.length || 0 },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`vr-detail-tab${detailTab === tab.key ? ' active' : ''}`}
                    onClick={() => setDetailTab(tab.key)}
                  >
                    <Icon name={tab.icon} size="0.85rem" />
                    {tab.label}
                    {tab.badge > 0 && <span className="vr-detail-tab-badge">{tab.badge}</span>}
                  </button>
                ))}
              </div>

              <div className="vr-detail-tab-panel">
              {detailTab === 'recordings' && (() => {
                const pausedSummary = getPausedSummary(detailMeeting);
                return (
                  <>
                    <div className="vr-detail-section-row">
                      <span className="vr-detail-section-label">PARTICIPANTS &middot; {detailMeeting.participants?.length || 0}</span>
                      <button type="button" className="vr-detail-download-all-btn" onClick={() => handleDownloadAll(detailMeeting)}>
                        <DownloadIcon /> Download all
                      </button>
                    </div>

                    {(detailMeeting.participants || []).map((p) => {
                      const audioFile = (detailMeeting.audio_files || []).find((f) => f.participant_identity === p.participant_identity);
                      const localEntry = Object.values(detailMeeting.local_recording_participants || {})
                        .find((lp) => lp.participant_identity === p.participant_identity);

                      return (
                        <div className="vr-detail-participant" key={p.participant_identity || p.email}>
                          <div className="vr-detail-participant-header">
                            <Avatar user={{ _id: p.user_id, name: p.name, agency_id: user?.agency_id }} size="sm" />
                            <div className="vr-detail-participant-info">
                              <span className="vr-detail-participant-name">{p.name || p.email || 'Participant'}</span>
                              <span className="vr-detail-participant-role">{getParticipantRoleLabel(detailMeeting, p)}</span>
                            </div>
                            {localEntry && (
                              <span className={`vr-detail-status-pill vr-detail-status-pill-${localEntry.status === 'completed' ? 'complete' : localEntry.status === 'failed' ? 'failed' : 'uploading'}`}>
                                {localEntry.status === 'completed' ? <><Icon name="ok" size="0.65rem" /> Complete</> : localEntry.status === 'failed' ? 'Failed' : 'Uploading'}
                              </span>
                            )}
                          </div>

                          {audioFile && (
                            <div className="vr-detail-file-row">
                              <span className="vr-detail-file-icon-wrap">
                                <Icon
                                  name="mic"
                                  size="0.9rem"
                                  className={`vr-detail-file-icon ${(audioFile.formats?.mp3 || audioFile.url) ? 'vr-detail-file-icon-complete' : 'vr-detail-file-icon-progress'}`}
                                />
                              </span>
                              <div className="vr-detail-file-info">
                                <span className="vr-detail-file-title">Server Recording</span>
                                <span className="vr-detail-file-sub">
                                  Server
                                  {getFileExtLabel(audioFile) ? ` · ${getFileExtLabel(audioFile)}` : ''}
                                  {formatFileDuration(audioFile.duration) ? ` · ${formatFileDuration(audioFile.duration)}` : ''}
                                  {' · used for the transcript'}
                                </span>
                              </div>
                              {(audioFile.formats?.mp3 || audioFile.url) && (
                                <a
                                  className="vr-detail-download-btn"
                                  href={audioFile.formats?.mp3 || audioFile.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Download"
                                >
                                  <DownloadIcon />
                                </a>
                              )}
                            </div>
                          )}

                          {localEntry && (
                            <div className="vr-detail-file-row">
                              <span className="vr-detail-file-icon-wrap">
                                <CameraIcon
                                  className={`vr-detail-file-icon ${(localEntry.status === 'completed' && localEntry.url) ? 'vr-detail-file-icon-complete' : ''}`}
                                />
                              </span>
                              <div className="vr-detail-file-info">
                                <span className="vr-detail-file-title">Local Recording</span>
                                <span className="vr-detail-file-sub">
                                  {localEntry.has_video ? 'Video + audio' : 'Audio'} · for editing
                                  {localEntry.status === 'uploading' || localEntry.status === 'processing' ? ' · uploading' : ''}
                                  {localEntry.status === 'failed' ? ' · failed' : ''}
                                </span>
                              </div>
                              {localEntry.status === 'completed' && localEntry.url && (
                                <a
                                  className="vr-detail-download-btn"
                                  href={localEntry.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Download"
                                >
                                  <DownloadIcon />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {pausedSummary && (
                      <div className="vr-detail-notice">
                        <Icon name="info-circled" size="0.9rem" />
                        {pausedSummary.count} paused segment{pausedSummary.count > 1 ? 's' : ''}
                        {pausedSummary.minutes ? ` (${pausedSummary.minutes} min total)` : ''} were left out of the transcript.
                      </div>
                    )}
                  </>
                );
              })()}

              {detailTab === 'transcript' && (
                detailMeeting.transcript ? (
                  <div className="vr-detail-transcript">
                    <button type="button" className="vr-detail-copy-btn" onClick={() => copyTranscript(detailMeeting.transcript)}>
                      <Icon name="doc" size="0.85rem" /> Copy
                    </button>
                    <pre className="vr-detail-transcript-text">{detailMeeting.transcript}</pre>
                  </div>
                ) : (
                  <p className="meeting-info-empty">No transcript available yet.</p>
                )
              )}

              {detailTab === 'notes' && (
                detailNotes.loading ? (
                  <div className="meeting-info-loading">
                    <span className="spinner-small"></span> Loading&hellip;
                  </div>
                ) : detailNotes.items.length === 0 ? (
                  <p className="meeting-info-empty">No notes were taken during this meeting.</p>
                ) : (
                  <div className="meeting-info-list">
                    {detailNotes.items.map((item) => (
                      <div key={item.id} className={`meeting-info-entry ${item.type === 'flag' ? 'flagged' : ''}`}>
                        <div className="meeting-info-entry-meta">
                          {item.type === 'flag' ? (
                            <>
                              <Icon name="bookmark" size="0.8rem" /> Flagged Moment &middot; {formatElapsed(item.elapsed_seconds)}
                            </>
                          ) : (
                            <>{item.author_name} &middot; {formatDateTime(item.created_at)}</>
                          )}
                        </div>
                        <div
                          className="meeting-info-entry-text"
                          dangerouslySetInnerHTML={{ __html: renderFormattedText(item.text) }}
                        />
                      </div>
                    ))}
                  </div>
                )
              )}

              {detailTab === 'chat' && (
                detailChat.loading ? (
                  <div className="meeting-info-loading">
                    <span className="spinner-small"></span> Loading&hellip;
                  </div>
                ) : detailChat.items.length === 0 ? (
                  <p className="meeting-info-empty">No chat messages were sent during this meeting.</p>
                ) : (
                  <div className="meeting-info-list">
                    {detailChat.items.map((item) => (
                      <div key={item.id} className="meeting-info-entry">
                        <div className="meeting-info-entry-meta">
                          {item.sender_name}{item.is_guest ? ' (Guest)' : ''} &middot; {formatDateTime(item.created_at)}
                        </div>
                        <div
                          className="meeting-info-entry-text"
                          dangerouslySetInnerHTML={{ __html: renderFormattedText(item.text) }}
                        />
                      </div>
                    ))}
                  </div>
                )
              )}
              </div>
            </div>

            <div className="vr-detail-footer">
              <button
                type="button"
                className="vr-detail-delete-btn"
                onClick={() => {
                  handleDelete(detailMeeting.meeting_id);
                  closeDetailPanel();
                }}
              >
                <Icon name="trash-empty" size="0.9rem" />
                Delete recording...
              </button>
              {detailMeeting.room_status === 'active' ? (
                <button
                  type="button"
                  className="vr-detail-footer-hint vr-detail-footer-hint-active"
                  onClick={() => handleJoinMeeting(detailMeeting.meeting_id)}
                >
                  Meetings open in a new tab
                  <Icon name="go-to-link" size="0.8rem" />
                </button>
              ) : (
                <span className="vr-detail-footer-hint">
                  Meetings open in a new tab
                  <Icon name="go-to-link" size="0.8rem" />
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveKitRecordings;
